/**
 * PaymentIcons.tsx — Payment Method Icons
 * 
 * Data-driven — reads accepted payment methods from admin config.
 * Shows clean pill-style badges for each method.
 * Falls back to common methods if none configured.
 */
import React from 'react';

interface PaymentIconsProps {
  data: {
    methods?: string[];
    layout?: 'inline' | 'wrap';
    variant?: string;
    content?: { variant?: string };
    [key: string]: unknown;
  };
  styles?: string;
  variant?: string;
}

const DEFAULT_METHODS = ['Visa', 'Mastercard', 'Amex', 'PayPal', 'Apple Pay', 'Google Pay'];

export const PaymentIcons: React.FC<PaymentIconsProps> = ({ data, styles, variant }) => {
  const isBentoDark = variant === 'bento-dark';
  const { methods } = data ?? {};

  // Variant: row (default — plain inline) | row-bordered (each mark in a
  // hairline pill) | centered-cluster (wraps + centred for narrow columns).
  const variantRaw = data?.content?.variant ?? data?.variant ?? variant ?? 'row';
  const layoutVariant: 'row' | 'row-bordered' | 'centered-cluster' =
    ['row','row-bordered','centered-cluster'].includes(variantRaw)
      ? variantRaw as 'row' | 'row-bordered' | 'centered-cluster'
      : 'row';

  const displayMethods = methods && methods.length > 0 ? methods : DEFAULT_METHODS;

  const wrapperClass = layoutVariant === 'centered-cluster'
    ? 'flex flex-wrap items-center justify-center gap-1.5'
    : 'flex items-center gap-1.5 flex-wrap';
  const showBorder = layoutVariant === 'row-bordered';
  // Payment chips sit in the footer mid/payment row — chrome-scoped text
  // first so the chip name reads against the (typically navy) footer bg.
  const baseChipClass = `px-3 py-1.5 rounded text-[9px] font-bold uppercase tracking-widest transition-colors ${isBentoDark
    ? 'text-[var(--color-footer-text,var(--text-muted))] bg-[color-mix(in_srgb,var(--surface-1)_2%,transparent)]'
    : 'text-[var(--color-footer-text,var(--text-muted,#64748b))] bg-[var(--style-chip-bg,var(--surface-2,#1e293b))]'
  }`;
  const borderClass = showBorder
    ? (isBentoDark
        ? 'border border-[color-mix(in_srgb,var(--glass-border)_10%,transparent)]'
        : 'border border-[var(--glass-border,rgba(255,255,255,0.08))]')
    : '';

  return (
    <div className={`${wrapperClass} ${styles ?? ''}`} data-variant={layoutVariant}>
      {displayMethods.map(method => (
        <span key={method} className={`${baseChipClass} ${borderClass}`}>
          {method}
        </span>
      ))}
    </div>
  );
};

export default PaymentIcons;
